import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SettingsIcon from '@mui/icons-material/Settings';
import { formatNcmCode } from 'utils/formatters/ncmFormatter';
import { formatDecimalToPercent } from 'views/admin/pm-main/comex/detail/utils/formatters';

const NcmReview = ({
  searchResults,
  selectedRow,
  onBack,
  formData,
  ncmDescription
}) => {
// If we have search results, use the selected NCM
// Otherwise, we're in edit mode with existing NCM data
  const selectedNcm = searchResults?.tec?.[selectedRow];
  const displayCode = selectedNcm?.ncm?.codigo || formData.ncm_code;
  const displayDescription = selectedNcm?.ncm?.descricao || ncmDescription || 'NCM details not available';
  const displayUnitMeasure = selectedNcm?.ncm?.unidadeMedida?.descricao || 'N/A';

  if (!displayCode) return null;

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{ mb: 2 }}
      >
        Back to Customize
      </Button>

      {/* NCM Details - Compact layout */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 1.5,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.5)' : 'rgba(33, 150, 243, 0.25)'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'flex-start',
            bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.08)' : 'rgba(33, 150, 243, 0.04)',
          }}
        >
          <ReceiptIcon color="primary" sx={{ mr: 2, mt: 0.5 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight="medium">{formatNcmCode(displayCode)}</Typography>
            <Typography variant="body2" color="text.secondary">
              {displayDescription}
            </Typography>
          </Box>
        </Box>

        {/* Details */}
        <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">NCM Code</Typography>
              <Typography variant="body2" fontWeight="medium">{formatNcmCode(displayCode)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Unit of Measure</Typography>
              <Typography variant="body2">{displayUnitMeasure}</Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Custom Tax Values Summary - Compact layout */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 1.5,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            bgcolor: theme => theme.palette.mode === 'dark'
              ? 'rgba(0, 150, 136, 0.15)'
              : 'rgba(0, 150, 136, 0.06)',
            borderBottom: '1px solid',
            borderColor: theme => theme.palette.mode === 'dark'
              ? 'rgba(0, 150, 136, 0.3)'
              : 'rgba(0, 150, 136, 0.2)',
          }}
        >
          <SettingsIcon sx={{ color: 'success.main' }} />
          <Typography
            variant="subtitle1"
            fontWeight="medium"
            color="text.primary"
          >
            Custom Tax Values
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12} sm={8} md={6}>
              <Typography variant="caption" color="text.secondary">PIS/COFINS Reduction Base</Typography>
              <Typography variant="body2" fontWeight="medium">
                {formData.reducao_base_pis_cofins != null && formData.reducao_base_pis_cofins !== '' ? formatDecimalToPercent(parseFloat(formData.reducao_base_pis_cofins)) : 'Not set'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                Reduction percentage for PIS/COFINS calculation
              </Typography>
            </Grid>
            <Grid item xs={12} sm={8} md={6}>
              <Typography variant="caption" color="text.secondary">Majoração COFINS</Typography>
              <Typography variant="body2" fontWeight="medium">
                {formData.majoracao_cofins != null && formData.majoracao_cofins !== '' ? formatDecimalToPercent(parseFloat(formData.majoracao_cofins)) : 'Not set'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                COFINS surcharge rate. Cost only, does not generate credit.
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>

    </Box>
  );
};

export default NcmReview;
